import type {
  CalendarDay,
  CalendarSummary,
  HolidayFormInput,
  HolidayRecord,
  HolidayType,
  MonthlyCalendarData,
  WorkingDayConfig,
} from '../types/officeCalendar';
import {
  BUSINESS_TIME_ZONE,
  calendarDateFromApi,
  parseCalendarDateInput,
  todayBusinessCalendarDate,
} from './dateTime';

/** Normalize holiday dates from API (handles ISO UTC-shifted mysql DATE values). */
export function holidayDateFromApi(value: unknown): string {
  const normalized = calendarDateFromApi(value);
  if (normalized) return normalized;
  if (value === null || value === undefined || value === '') return '';
  return String(value).slice(0, 10);
}

/** Stored in org_holidays.description so existing holiday APIs accept week offs. */
export const WEEK_OFF_DESCRIPTION = 'WEEK_OFF';

/** Default stored name; Weekly Off UI does not collect a custom name. */
export const WEEK_OFF_NAME = 'Weekly Off';

export function isWeekOffMarker(value: string | null | undefined): boolean {
  return String(value ?? '').trim().toUpperCase() === 'WEEK_OFF';
}

export function isWeekOffHolidayType(
  type: HolidayType | string | null | undefined,
): boolean {
  return isWeekOffMarker(type);
}

export function isCalendarWeekOff(entry: {
  holidayType?: HolidayType | string | null;
  description?: string | null;
} | null | undefined): boolean {
  if (!entry) return false;
  return isWeekOffMarker(entry.holidayType) || isWeekOffMarker(entry.description);
}

/** Map UI Holiday/Weekly Off to the payload the existing holiday API accepts. */
export function toHolidayApiPayload(input: {
  holidayName: string;
  holidayDate: string;
  holidayType: HolidayType;
  description: string;
}): {
  holidayName: string;
  holidayDate: string;
  holidayType: HolidayType;
  description: string;
} {
  const isWeekOff =
    isWeekOffMarker(input.holidayType) || isWeekOffMarker(input.description);
  return {
    holidayName: isWeekOff
      ? input.holidayName.trim() || WEEK_OFF_NAME
      : input.holidayName.trim(),
    holidayDate: input.holidayDate,
    holidayType: isWeekOff ? 'OTHER' : input.holidayType,
    description: isWeekOff ? WEEK_OFF_DESCRIPTION : input.description.trim(),
  };
}

function monthPrefix(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}-`;
}

export function isDateInMonth(dateStr: string, year: number, month: number): boolean {
  return dateStr.startsWith(monthPrefix(year, month));
}

/** Unique, sorted dates in the given month for a single Weekly Off apply. */
export function uniqueDatesInMonth(
  dates: string[],
  year: number,
  month: number,
): string[] {
  return [...new Set(dates.filter((date) => isDateInMonth(date, year, month)))].sort();
}

/** Holiday / Weekly Off may be added or edited on today and future dates only. */
export function isEditableCalendarDate(
  dateStr: string,
  today: string = todayBusinessCalendarDate(),
): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(dateStr) && dateStr >= today;
}

export function monthHasEditableCalendarDates(
  year: number,
  month: number,
  today: string = todayBusinessCalendarDate(),
): boolean {
  const lastDay = new Date(year, month, 0).getDate();
  const lastDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return isEditableCalendarDate(lastDate, today);
}

export function defaultEditableCalendarDate(
  year: number,
  month: number,
  preferredDate: string | null | undefined,
  today: string = todayBusinessCalendarDate(),
): string | null {
  if (
    preferredDate &&
    isDateInMonth(preferredDate, year, month) &&
    isEditableCalendarDate(preferredDate, today)
  ) {
    return preferredDate;
  }
  if (isDateInMonth(today, year, month)) return today;
  const firstDate = `${year}-${String(month).padStart(2, '0')}-01`;
  return isEditableCalendarDate(firstDate, today) ? firstDate : null;
}

export function weekOffFormPayloads(
  dates: string[],
  year: number,
  month: number,
  today: string = todayBusinessCalendarDate(),
): HolidayFormInput[] {
  return uniqueDatesInMonth(dates, year, month)
    .filter((holidayDate) => isEditableCalendarDate(holidayDate, today))
    .map((holidayDate) => ({
      holidayName: WEEK_OFF_NAME,
      holidayDate,
      holidayType: 'WEEK_OFF',
      description: '',
    }));
}

function buildWorkingDaySet(config: WorkingDayConfig[]): Record<number, boolean> {
  const set: Record<number, boolean> = {};
  for (const row of config) {
    set[row.dayOfWeek] = row.isWorkingDay === true || Number(row.isWorkingDay) === 1;
  }
  return set;
}

function dayOfWeekMonFirst(dateStr: string): number {
  const parsed = parseCalendarDateInput(dateStr);
  if (!parsed) return 1;
  const jsDay = parsed.getDay();
  return jsDay === 0 ? 7 : jsDay;
}

/** Build calendar grid on the client from holidays + working-day config. */
export function buildMonthlyCalendar(
  year: number,
  month: number,
  holidays: HolidayRecord[],
  workingDaysConfig: WorkingDayConfig[],
): { calendarDays: CalendarDay[]; summary: CalendarSummary } {
  const normalizedHolidays = holidays.map((h) => ({
    ...h,
    holidayDate: holidayDateFromApi(h.holidayDate),
  }));
  const holidayByDate = new Map(
    normalizedHolidays.map((h) => [h.holidayDate, h]),
  );
  const workingDaySet = buildWorkingDaySet(workingDaysConfig);
  const todayStr = todayBusinessCalendarDate();
  const daysInMonth = new Date(year, month, 0).getDate();

  const calendarDays: CalendarDay[] = [];
  let workingDays = 0;
  let holidayCount = 0;
  let weekends = 0;

  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dow = dayOfWeekMonFirst(dateStr);
    const holiday = holidayByDate.get(dateStr) ?? null;
    const isConfiguredWeekOff = workingDaySet[dow] === false;
    const isNamedWeekOff = isCalendarWeekOff(holiday);
    const isWeekend = isNamedWeekOff || (!holiday && isConfiguredWeekOff);
    const isToday = dateStr === todayStr;

    let dayType: CalendarDay['dayType'];
    if (holiday && !isNamedWeekOff) {
      dayType = 'HOLIDAY';
      holidayCount++;
    } else if (isWeekend) {
      dayType = 'WEEKEND';
      weekends++;
    } else {
      dayType = 'WORKING_DAY';
      workingDays++;
    }

    calendarDays.push({
      date: dateStr,
      dayOfWeek: dow,
      dayName: weekdayNameFromRecord(holiday, dateStr),
      displayDate: displayDateFromRecord(holiday, dateStr),
      dayType,
      isWeekend,
      isToday,
      holidayId: holiday?.holidayId ?? null,
      holidayName: holiday?.holidayName ?? null,
      holidayType: isNamedWeekOff ? 'WEEK_OFF' : holiday?.holidayType ?? null,
      holidayDescription: isNamedWeekOff ? null : holiday?.description ?? null,
    });
  }

  return {
    calendarDays,
    summary: {
      workingDays,
      holidays: holidayCount,
      weekends,
      totalDays: daysInMonth,
    },
  };
}

export function normalizeMonthlyCalendarData(
  data: MonthlyCalendarData,
): MonthlyCalendarData {
  const holidays = (data.holidays ?? []).map((h) => ({
    ...h,
    holidayDate: holidayDateFromApi(h.holidayDate),
    dayName: textOrNull(h.dayName ?? (h as { day_name?: unknown }).day_name),
    displayDate: textOrNull(
      h.displayDate ?? (h as { display_date?: unknown }).display_date,
    ),
  }));
  const workingDaysConfig = data.workingDaysConfig ?? [];
  const built = buildMonthlyCalendar(
    data.year,
    data.month,
    holidays,
    workingDaysConfig,
  );

  return {
    ...data,
    notes: extractMonthNotes(data),
    holidays,
    workingDaysConfig,
    calendarDays: built.calendarDays,
    summary: built.summary,
  };
}

function textOrNull(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).trim();
  return text || null;
}

export function extractMonthNotes(payload: unknown): string {
  if (!payload || typeof payload !== 'object') return '';
  const row = payload as Record<string, unknown>;
  const value =
    row.notes ?? row.monthNotes ?? row.month_notes ?? row.calendarNotes;
  if (typeof value === 'string') return value;
  if (value == null) return '';
  return String(value);
}

export function formatHolidayDisplayDate(dateStr: string): string {
  const parsed = parseCalendarDateInput(dateStr);
  if (!parsed) return dateStr;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: BUSINESS_TIME_ZONE,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).formatToParts(parsed);
  const day = parts.find((part) => part.type === 'day')?.value ?? '';
  const month = (parts.find((part) => part.type === 'month')?.value ?? '')
    .replace('.', '')
    .slice(0, 3);
  const year = parts.find((part) => part.type === 'year')?.value ?? '';
  return [day, month, year].filter(Boolean).join(' ');
}

export function formatWeekdayName(dateStr: string): string {
  const parsed = parseCalendarDateInput(dateStr);
  if (!parsed) return '';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: BUSINESS_TIME_ZONE,
    weekday: 'long',
  }).format(parsed);
}

function displayDateFromRecord(
  holiday: Pick<HolidayRecord, 'displayDate'> | null,
  dateStr: string,
): string {
  return textOrNull(holiday?.displayDate) ?? formatHolidayDisplayDate(dateStr);
}

function weekdayNameFromRecord(
  holiday: Pick<HolidayRecord, 'dayName'> | null,
  dateStr: string,
): string {
  return textOrNull(holiday?.dayName) ?? formatWeekdayName(dateStr);
}

export type CalendarLegendStatus = 'working' | 'holiday' | 'weekend' | 'today';

export const CALENDAR_LEGEND_STATUSES: CalendarLegendStatus[] = [
  'working',
  'holiday',
  'weekend',
  'today',
];

/** Same status used by the Legend and the calendar date number. */
export function calendarLegendStatus(
  day: Pick<CalendarDay, 'dayType' | 'isToday'>,
): CalendarLegendStatus {
  if (day.dayType === 'HOLIDAY') return 'holiday';
  if (day.dayType === 'WEEKEND') return 'weekend';
  if (day.isToday) return 'today';
  return 'working';
}

export function calendarLegendColor(
  day: Pick<CalendarDay, 'dayType' | 'isToday'>,
  colors: Record<CalendarLegendStatus, string>,
): string {
  return colors[calendarLegendStatus(day)];
}

/** Dates that Office Calendar treats as Holiday or Weekly Off. */
export function officeCalendarOffDates(
  calendarDays: CalendarDay[] | null | undefined,
): Set<string> {
  const off = new Set<string>();
  for (const day of calendarDays ?? []) {
    if (day.dayType !== 'WORKING_DAY') {
      off.add(day.date);
    }
  }
  return off;
}

export function calendarLegendLabel(status: CalendarLegendStatus): string {
  if (status === 'holiday') return 'Holiday';
  if (status === 'weekend') return 'Weekly Off';
  if (status === 'today') return 'Today';
  return 'Working Day';
}

/** e.g. `15 Sep 2026 - Tuesday` from the stored calendar date (and optional DB day name). */
export function formatHolidayDateAndDay(
  dateStr: string,
  dayName?: string | null,
  displayDate?: string | null,
): string {
  const datePart = textOrNull(displayDate) ?? formatHolidayDisplayDate(dateStr);
  const weekday = textOrNull(dayName) ?? formatWeekdayName(dateStr);
  if (!datePart) return weekday;
  if (!weekday) return datePart;
  return `${datePart} - ${weekday}`;
}

export const UPCOMING_NAMED_HOLIDAY_LIMIT = 3;
export const UPCOMING_HOLIDAY_LOOKAHEAD_MONTHS = 12;

function holidayNameOrNull(value: unknown): string | null {
  return textOrNull(value);
}

/** Named holidays on/after today. Weekly offs from org_holidays are excluded. */
export function selectUpcomingNamedHolidays(
  holidays: HolidayRecord[],
  today: string = todayBusinessCalendarDate(),
  limit: number = UPCOMING_NAMED_HOLIDAY_LIMIT,
): HolidayRecord[] {
  const max = Number.isInteger(limit) && limit > 0 ? limit : UPCOMING_NAMED_HOLIDAY_LIMIT;
  return holidays
    .map(holiday => ({
      ...holiday,
      holidayDate: holidayDateFromApi(holiday.holidayDate),
      holidayName: holidayNameOrNull(holiday.holidayName) ?? '',
      dayName: textOrNull(holiday.dayName),
      displayDate: textOrNull(holiday.displayDate),
    }))
    .filter(
      holiday =>
        Boolean(holiday.holidayDate) &&
        holiday.holidayDate >= today &&
        Boolean(holiday.holidayName) &&
        !isCalendarWeekOff(holiday),
    )
    .sort((left, right) => {
      const byDate = left.holidayDate.localeCompare(right.holidayDate);
      if (byDate !== 0) return byDate;
      return left.holidayId - right.holidayId;
    })
    .slice(0, max);
}

export function nextCalendarMonth(
  year: number,
  month: number,
): { year: number; month: number } {
  if (month >= 12) return { year: year + 1, month: 1 };
  return { year, month: month + 1 };
}
